import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, AlertTriangle, Users, Mail, Search, Shield, UserX, GraduationCap, Ban } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  fetchQualitySummary,
  listQualityContacts,
  listEmailDupes,
  listOrphanMemberships,
  fetchAudienceAuditSummary,
  type QualitySummary,
  type QualityContact,
  type EmailDupeCluster,
  type OrphanMembership,
  type AudienceAuditSummary,
} from '../../lib/supabase';
import { PageHeader } from '../../components/PageHeader';
import { formatNumber } from '../../lib/format';

type IssueFilter =
  | 'all'
  | 'any_issue'
  | 'missing_first_name'  | 'missing_last_name'
  | 'missing_studio'      | 'missing_birth_date'
  | 'missing_gender'      | 'suspicious_email'
  | 'first_name_has_whitespace' | 'last_name_has_whitespace'
  | 'birth_date_unrealistic';

const ISSUE_LABELS: Record<Exclude<IssueFilter, 'all'>, string> = {
  any_issue:                  'Irgendein Problem',
  missing_first_name:         'Vorname fehlt',
  missing_last_name:          'Nachname fehlt',
  missing_studio:             'Studio fehlt',
  missing_birth_date:         'Geburtsdatum fehlt',
  missing_gender:             'Geschlecht fehlt',
  suspicious_email:           'Verdächtige Email',
  first_name_has_whitespace:  'Vorname mit Leerzeichen',
  last_name_has_whitespace:   'Nachname mit Leerzeichen',
  birth_date_unrealistic:     'Geburtsdatum unplausibel',
};

const SUMMARY_TILES: Array<{ key: keyof QualitySummary; label: string; tone: 'warning' | 'danger' | 'info' }> = [
  { key: 'any_issue_active',         label: 'Mind. 1 Problem',           tone: 'warning' },
  { key: 'missing_studio',           label: 'Studio fehlt',              tone: 'danger'  },
  { key: 'missing_birth_date',       label: 'Geburtsdatum fehlt',        tone: 'warning' },
  { key: 'missing_gender',           label: 'Geschlecht fehlt',          tone: 'warning' },
  { key: 'suspicious_email',         label: 'Verdächtige Email',         tone: 'danger'  },
  { key: 'first_name_whitespace',    label: 'Vorname mit Leerzeichen',    tone: 'info'    },
  { key: 'last_name_whitespace',     label: 'Nachname mit Leerzeichen',   tone: 'info'    },
  { key: 'birth_date_unrealistic',   label: 'Geburtsdatum unplausibel',  tone: 'warning' },
  { key: 'hard_bounced',             label: 'Hard-Bounces',              tone: 'danger'  },
  { key: 'unsubscribed',             label: 'Abgemeldet',                tone: 'info'    },
];

const ORPHAN_REASON_LABELS: Record<OrphanMembership['reason'], string> = {
  no_email:                  'Keine E-Mail im CRM',
  invalid_email_syntax:      'E-Mail-Syntax ungültig',
  shared_email_other_person: 'E-Mail wird bereits von anderer Person genutzt',
};

function toneColors(tone: 'warning' | 'danger' | 'info') {
  if (tone === 'danger')  return { bg: 'var(--danger-light)',  fg: 'var(--danger-dark)'  };
  if (tone === 'warning') return { bg: 'var(--warning-light, #fff7e6)', fg: 'var(--warning-dark, #b25e00)' };
  return                  { bg: 'var(--primary-light)', fg: 'var(--primary)' };
}

export function DataQuality() {
  const [tab, setTab] = useState<'audience' | 'issues' | 'families' | 'orphans'>('audience');
  const [issueFilter, setIssueFilter] = useState<IssueFilter>('any_issue');
  const [search, setSearch] = useState('');
  const [orphanReason, setOrphanReason] = useState<'all' | OrphanMembership['reason']>('all');

  const summaryQuery = useQuery<QualitySummary | null, Error>({
    queryKey: ['mail-quality-summary'],
    queryFn:  () => fetchQualitySummary(),
  });
  const audienceQuery = useQuery<AudienceAuditSummary | null, Error>({
    queryKey: ['mail-audience-audit'],
    queryFn:  () => fetchAudienceAuditSummary(),
    enabled:  tab === 'audience',
  });
  const issuesQuery = useQuery<QualityContact[], Error>({
    queryKey: ['mail-quality-contacts', issueFilter],
    queryFn:  () => listQualityContacts(issueFilter, 500),
    enabled:  tab === 'issues',
  });
  const familiesQuery = useQuery<EmailDupeCluster[], Error>({
    queryKey: ['mail-email-dupes'],
    queryFn:  () => listEmailDupes(),
    enabled:  tab === 'families',
  });
  const orphansQuery = useQuery<OrphanMembership[], Error>({
    queryKey: ['mail-orphans'],
    queryFn:  () => listOrphanMemberships(),
    enabled:  tab === 'orphans',
  });

  const filteredIssues = useMemo<QualityContact[]>(() => {
    if (!issuesQuery.data) return [];
    if (!search.trim()) return issuesQuery.data;
    const q = search.trim().toLowerCase();
    return issuesQuery.data.filter((c) =>
      c.email.toLowerCase().includes(q) ||
      (c.first_name?.toLowerCase().includes(q) ?? false) ||
      (c.last_name?.toLowerCase().includes(q) ?? false) ||
      (c.organization?.toLowerCase().includes(q) ?? false)
    );
  }, [issuesQuery.data, search]);

  const filteredOrphans = useMemo<OrphanMembership[]>(() => {
    if (!orphansQuery.data) return [];
    let rows: OrphanMembership[] = orphansQuery.data;
    if (orphanReason !== 'all') rows = rows.filter((r) => r.reason === orphanReason);
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r: OrphanMembership) =>
      (r.email?.toLowerCase().includes(q) ?? false) ||
      (r.first_name?.toLowerCase().includes(q) ?? false) ||
      (r.last_name?.toLowerCase().includes(q) ?? false) ||
      (r.organization?.toLowerCase().includes(q) ?? false)
    );
  }, [orphansQuery.data, orphanReason, search]);

  return (
    <div>
      <PageHeader
        title="Datenqualität"
        subtitle="Überblick über Lücken und Inkonsistenzen in den Mail-Kontakten"
      />

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        <ShieldAlert size={16} />
        <div>
          <strong>Read-only:</strong> Daten werden im CRM gepflegt und stuendlich nach hier synchronisiert.
          Korrekturen bitte direkt im CRM — sie erscheinen mit dem nächsten Sync.
        </div>
      </div>

      {summaryQuery.data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <KpiTile label="Aktive Kontakte" value={summaryQuery.data.total_active} tone="info" icon={<Users size={18} />} />
          {SUMMARY_TILES.map((t) => (
            <KpiTile key={t.key} label={t.label} value={(summaryQuery.data?.[t.key] as number) ?? 0} tone={t.tone} icon={<AlertTriangle size={18} />} />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid var(--border-color)', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <TabButton active={tab === 'audience'} onClick={() => setTab('audience')} label="Empfänger-Audit"        count={audienceQuery.data?.postfach.mailable} />
        <TabButton active={tab === 'issues'}    onClick={() => setTab('issues')}    label="Probleme pro Kontakt"  count={summaryQuery.data?.any_issue_active} />
        <TabButton active={tab === 'families'} onClick={() => setTab('families')} label="Familien-Postfächer"     count={familiesQuery.data?.length} />
        <TabButton active={tab === 'orphans'}  onClick={() => setTab('orphans')}  label="Nicht erreichbar"        count={orphansQuery.data?.length} />
      </div>

      {tab === 'audience' && (
        <AudienceAuditTab data={audienceQuery.data} isLoading={audienceQuery.isLoading} error={audienceQuery.error} />
      )}

      {tab === 'issues' && (
        <div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
            <select className="form-select" style={{ maxWidth: 280 }} value={issueFilter} onChange={(e) => setIssueFilter(e.target.value as IssueFilter)}>
              <option value="any_issue">Alle mit irgendeinem Problem</option>
              <option value="all">Alle Kontakte</option>
              {(Object.entries(ISSUE_LABELS) as Array<[Exclude<IssueFilter,'all'>, string]>)
                .filter(([k]) => k !== 'any_issue')
                .map(([k, label]) => (<option key={k} value={k}>Nur: {label}</option>))}
            </select>
            <div className="search-input-wrapper" style={{ flex: 1, minWidth: 240 }}>
              <Search size={16} />
              <input className="search-input" placeholder="E-Mail / Name / Studio suchen..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          {issuesQuery.isLoading && <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="loading-spinner" /></div>}
          {issuesQuery.error && <div className="alert alert-danger">Fehler: {issuesQuery.error.message}</div>}

          {issuesQuery.data && (
            <div style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
              <table className="data-table" style={{ border: 'none', borderRadius: 0 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>E-Mail</th><th>Name</th><th>Studio</th><th>Probleme</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIssues.map((c) => (
                    <tr key={c.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{c.email}</td>
                      <td>{[c.first_name, c.last_name].filter(Boolean).join(' ') || <em style={{ color: 'var(--text-tertiary)' }}>—</em>}</td>
                      <td>{c.organization ?? <em style={{ color: 'var(--text-tertiary)' }}>—</em>}</td>
                      <td><IssueBadges contact={c} /></td>
                    </tr>
                  ))}
                  {filteredIssues.length === 0 && !issuesQuery.isLoading && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-tertiary)' }}>Keine Treffer.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {issuesQuery.data && issuesQuery.data.length >= 500 && (
            <p className="form-hint" style={{ marginTop: '0.5rem' }}>⚠ Anzeige auf 500 Zeilen begrenzt. Filter genauer wählen, um andere Kontakte zu sehen.</p>
          )}
        </div>
      )}

      {tab === 'families' && (
        <div>
          <p className="form-hint" style={{ margin: '0 0 1rem' }}>
            Postfächer, die sich mehrere CRM-Personen teilen (typisch: Eltern-Mail für mehrere Kinder).
            <strong> Im Mail-Tool</strong> = die Person, die aktuell die E-Mail in Mail_contacts repräsentiert.
            <strong> Verloren</strong> = restliche Familienmitglieder (mit Migration 0011 aggregieren wir ihre Daten — Studio, Mitgliedsstatus —
            in <code>household_*</code>-Spalten, sodass Filter sie wieder einfangen).
          </p>

          {familiesQuery.isLoading && <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="loading-spinner" /></div>}
          {familiesQuery.error && <div className="alert alert-danger">Fehler: {familiesQuery.error.message}</div>}

          {familiesQuery.data && (
            <div style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
              <table className="data-table" style={{ border: 'none', borderRadius: 0 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>E-Mail</th><th>Personen</th><th>Im Mail-Tool</th><th>Verloren</th><th>Studios</th>
                  </tr>
                </thead>
                <tbody>
                  {familiesQuery.data.map((d: EmailDupeCluster) => {
                    const winner = (d.winner_name ?? '').trim();
                    const losers = (d.names ?? []).map((n) => n.trim()).filter((n) => n && n !== winner);
                    return (
                      <tr key={d.email}>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{d.email}</td>
                        <td><strong>{d.distinct_persons}</strong></td>
                        <td style={{ fontSize: '0.875rem' }}>
                          {winner ? (
                            <span className="status-badge" style={{ background: 'var(--success-light)', color: 'var(--success-dark)' }}>
                              {winner}{d.winner_organization ? ` (${d.winner_organization})` : ''}
                            </span>
                          ) : (
                            <em style={{ color: 'var(--text-tertiary)' }}>—</em>
                          )}
                        </td>
                        <td style={{ fontSize: '0.875rem' }}>
                          {losers.length === 0
                            ? <em style={{ color: 'var(--text-tertiary)' }}>—</em>
                            : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {losers.map((n) => (
                                  <span key={n} className="status-badge" style={{ background: 'var(--warning-light, #fff7e6)', color: 'var(--warning-dark, #b25e00)' }}>{n}</span>
                                ))}
                              </div>
                            )}
                        </td>
                        <td style={{ fontSize: '0.875rem' }}>{d.organizations?.filter(Boolean).join(', ')}</td>
                      </tr>
                    );
                  })}
                  {familiesQuery.data.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-tertiary)' }}>Keine geteilten Postfächer gefunden.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'orphans' && (
        <div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
            <select className="form-select" style={{ maxWidth: 360 }} value={orphanReason} onChange={(e) => setOrphanReason(e.target.value as 'all' | OrphanMembership['reason'])}>
              <option value="all">Alle Gründe</option>
              {(Object.entries(ORPHAN_REASON_LABELS) as Array<[OrphanMembership['reason'], string]>).map(([k, label]) => (
                <option key={k} value={k}>Nur: {label}</option>
              ))}
            </select>
            <div className="search-input-wrapper" style={{ flex: 1, minWidth: 240 }}>
              <Search size={16} />
              <input className="search-input" placeholder="E-Mail / Name / Studio suchen..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          {orphansQuery.isLoading && <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="loading-spinner" /></div>}
          {orphansQuery.error && <div className="alert alert-danger">Fehler: {orphansQuery.error.message}</div>}

          {orphansQuery.data && (
            <div style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
              <table className="data-table" style={{ border: 'none', borderRadius: 0 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Name</th><th>E-Mail (CRM)</th><th>Studio</th><th>Grund</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrphans.map((o: OrphanMembership) => (
                    <tr key={o.membership_id}>
                      <td>{[o.first_name, o.last_name].filter(Boolean).join(' ') || <em style={{ color: 'var(--text-tertiary)' }}>—</em>}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{o.email || <em style={{ color: 'var(--text-tertiary)' }}>—</em>}</td>
                      <td>{o.organization ?? <em style={{ color: 'var(--text-tertiary)' }}>—</em>}</td>
                      <td><span className="status-badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-dark)' }}>{ORPHAN_REASON_LABELS[o.reason]}</span></td>
                    </tr>
                  ))}
                  {filteredOrphans.length === 0 && !orphansQuery.isLoading && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-tertiary)' }}>Keine Treffer.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, tone, icon }: { label: string; value: number; tone: 'warning' | 'danger' | 'info'; icon?: React.ReactNode }) {
  const c = toneColors(tone);
  return (
    <div className="card" style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: c.bg, color: c.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon ?? <Mail size={18} />}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{formatNumber(value)}</div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count?: number | null }) {
  return (
    <button onClick={onClick} style={{
      padding: '0.625rem 1rem', background: 'none',
      border: 'none', borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
      color: active ? 'var(--primary)' : 'var(--text-secondary)',
      fontWeight: active ? 600 : 500, cursor: 'pointer', fontSize: '0.9375rem',
    }}>
      {label}
      {typeof count === 'number' && (
        <span style={{ marginLeft: 8, fontSize: '0.75rem', padding: '0.125rem 0.5rem', borderRadius: 999, background: active ? 'var(--primary-light)' : 'var(--surface-secondary)', color: active ? 'var(--primary)' : 'var(--text-secondary)' }}>
          {formatNumber(count)}
        </span>
      )}
    </button>
  );
}

function AudienceAuditTab({ data, isLoading, error }: { data: AudienceAuditSummary | null | undefined; isLoading: boolean; error: Error | null }) {
  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="loading-spinner" /></div>;
  }
  if (error) {
    return <div className="alert alert-danger"><AlertTriangle size={16} /><div>Fehler beim Laden: {error.message}</div></div>;
  }
  if (!data) {
    return <div className="alert alert-info"><AlertTriangle size={16} /><div>Keine Audit-Daten verfügbar.</div></div>;
  }
  const p = data.postfach;
  const pe = data.person;
  return (
    <div>
      <div className="alert" style={{ background: 'var(--success-light)', color: 'var(--success-dark)', marginBottom: '1rem' }}>
        <Shield size={16} />
        <div>
          <strong>{formatNumber(p.mailable)} Postfächer</strong> bekommen aktuell Marketing-Mails — davon
          {' '}<strong>{formatNumber(p.mailable_direct_member)}</strong> direkt als Mitglied,
          {' '}<strong>{formatNumber(p.mailable_family_only)}</strong> als Eltern-Postfach mit Kind-Mitgliedschaft.
          Probeschüler, Ex-Mitglieder und gekündigte Verträge sind automatisch ausgeschlossen.
        </div>
      </div>

      {/* KPIs: erreichbar + blockiert */}
      <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Versandbasis (Postfächer)
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <KpiTile label="Mailable gesamt"          value={p.mailable}                tone="info"    icon={<Mail size={18} />} />
        <KpiTile label="Direkt Mitglied"          value={p.mailable_direct_member}  tone="info"    icon={<Users size={18} />} />
        <KpiTile label="Über Familie erreichbar"  value={p.mailable_family_only}    tone="info"    icon={<Users size={18} />} />
        <KpiTile label="Manuell (Backoffice)"     value={p.manual_mailable}         tone="info"    icon={<Mail size={18} />} />
      </div>

      <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Automatisch ausgeschlossen
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <KpiTile label="Ex-Mitglieder / Inaktiv"       value={p.blocked_ex_mitglied}   tone="danger"  icon={<UserX size={18} />} />
        <KpiTile label="Interessenten (Probe)"         value={p.postbox_with_int_role} tone="warning" icon={<GraduationCap size={18} />} />
        <KpiTile label="Ex-Trainer ohne Vertrag"       value={p.blocked_ex_trainer}    tone="warning" icon={<UserX size={18} />} />
        <KpiTile label="Abgemeldet (Opt-Out)"          value={p.blocked_unsubscribed}  tone="info"    icon={<Ban size={18} />} />
        <KpiTile label="Hard Bounce"                    value={p.blocked_hard_bounce}   tone="danger"  icon={<AlertTriangle size={18} />} />
      </div>

      {/* Personen-Sicht */}
      <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        CRM-Personen-Sicht (eine Zeile pro Mitgliedschaft)
      </h3>
      <p className="form-hint" style={{ margin: '0 0 0.75rem' }}>
        Postfächer sind nach Familie zusammengefasst — diese Zahlen zeigen die echten Personen dahinter.
        Probetraining-Pipeline = Personen mit Rolle „Interessent" und ohne aktiven Vertrag.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <KpiTile label="Personen zahlungsaktiv"       value={pe.personen_aktiv}              tone="info"    icon={<Users size={18} />} />
        <KpiTile label="Probetraining-Pipeline"       value={pe.personen_probe_pipeline}     tone="warning" icon={<GraduationCap size={18} />} />
        <KpiTile label="Ex-Mitglieder (gekündigt)"    value={pe.personen_gekuendigt}         tone="danger"  icon={<UserX size={18} />} />
        <KpiTile label="Sonstige Inaktive"             value={pe.personen_sonstige_inaktiv}   tone="warning" icon={<AlertTriangle size={18} />} />
      </div>

      {/* Drill-down Links auf [Audit]-Segmente */}
      <div className="alert alert-info" style={{ marginBottom: '1.5rem' }}>
        <ShieldAlert size={16} />
        <div>
          <strong>Wer ist im Detail blockiert?</strong> Drei <strong>[Audit]</strong>-Gruppen unter
          {' '}<Link to="/segments" style={{ color: 'var(--primary)', fontWeight: 600 }}>Gruppen</Link>
          {' '}listen die betroffenen Postfächer namentlich auf:
          <em> Ex-Mitglieder & Inaktive</em>, <em>Interessenten / Probetraining-Pipeline</em>,
          <em> Trainer ohne aktive Mitgliedschaft</em>. Diese Gruppen sind nur zur Sichtbarkeit gedacht,
          ein Versand an sie ist trotzdem nicht möglich (Opt-In bleibt automatisch false).
        </div>
      </div>

      {/* Pro-Studio-Tabelle */}
      <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Aufschlüsselung pro Studio
      </h3>
      <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
        <table className="data-table" style={{ border: 'none', borderRadius: 0, minWidth: 720 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Studio</th>
              <th style={{ textAlign: 'right' }}>Mailable Postfächer</th>
              <th style={{ textAlign: 'right' }}>davon Familien-Mails</th>
              <th style={{ textAlign: 'right' }}>Personen zahlungsaktiv</th>
              <th style={{ textAlign: 'right' }}>Probetraining-Pipeline</th>
              <th style={{ textAlign: 'right' }}>Ex-Mitglieder</th>
            </tr>
          </thead>
          <tbody>
            {data.per_studio.map((s) => (
              <tr key={s.org}>
                <td style={{ fontWeight: 500 }}>{s.org}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}><strong>{formatNumber(s.mailable)}</strong></td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-tertiary)' }}>{formatNumber(s.davon_family_only)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatNumber(s.personen_aktiv)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: s.personen_probe > 0 ? 'var(--warning-dark, #b25e00)' : 'var(--text-tertiary)' }}>{formatNumber(s.personen_probe)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-tertiary)' }}>{formatNumber(s.personen_gekuendigt)}</td>
              </tr>
            ))}
            {data.per_studio.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-tertiary)' }}>Keine Studio-Daten.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="form-hint" style={{ marginTop: '0.5rem' }}>
        Studios werden über die <strong>Familien-Sicht</strong> gezählt — ein Postfach mit Eltern
        bei Charlottenburg und Kind bei Spandau zählt in beiden Studios mit. „Familien-Mails" =
        Postfach selbst nicht Mitglied, aber jemand im Haushalt schon (Eltern-Postfach für
        Kind-Mitgliedschaft).
      </p>
    </div>
  );
}

function IssueBadges({ contact }: { contact: QualityContact }) {
  const flags: Array<[keyof QualityContact, string]> = [
    ['missing_first_name', 'Vorname fehlt'],
    ['missing_last_name',  'Nachname fehlt'],
    ['missing_studio',     'Studio fehlt'],
    ['missing_birth_date', 'Geb.-Datum fehlt'],
    ['missing_gender',     'Geschlecht fehlt'],
    ['suspicious_email',   'Email verd.'],
    ['first_name_has_whitespace', 'Vorname Whitespace'],
    ['last_name_has_whitespace',  'Nachname Whitespace'],
    ['birth_date_unrealistic',    'Geb.-Datum unplaus.'],
  ];
  const active = flags.filter(([k]) => Boolean(contact[k]));
  if (active.length === 0) return <em style={{ color: 'var(--text-tertiary)' }}>—</em>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {active.map(([k, label]) => (
        <span key={String(k)} className="status-badge" style={{ background: 'var(--warning-light, #fff7e6)', color: 'var(--warning-dark, #b25e00)', fontSize: '0.7rem' }}>{label}</span>
      ))}
    </div>
  );
}
